import { InputGroupText } from "@repo/ui/components/ui/input-group";
import { Kbd } from "@repo/ui/components/ui/kbd";
import { cn } from "@repo/ui/lib/utils";
import { useHotkey } from "@tanstack/react-hotkeys";
import { useEffect, useState } from "react";
import { useFloatingPromptActions, useFloatingPromptOpen } from "@/app/command-palette/use-floating-prompt-store";
import { AgentChat, useAgentChatContext } from "@/features/chat/components/agent-chat";
import { AgentChatSubjectBanner } from "@/features/chat/components/agent-chat/subject-banner";

const MAX_MESSAGES_HEIGHT = 256;

/** Hidden when empty; otherwise sizes to content up to MAX_MESSAGES_HEIGHT, then scrolls —
 * both via CSS alone (MessageScroller's own flex internals size correctly without a
 * measured, JS-written pixel height). */
function FloatingMessages() {
  const { state } = useAgentChatContext();

  if (state.messages.length === 0) return null;

  return (
    <div className="flex flex-col overflow-hidden" style={{ maxHeight: MAX_MESSAGES_HEIGHT }}>
      <AgentChat.Thread className="flex-1" />
    </div>
  );
}

/** Focus the input when the overlay opens; also re-focus once the enter animation has scheduled. */
function OverlayAutoFocus({ open }: { open: boolean }) {
  const { meta } = useAgentChatContext();
  useEffect(() => {
    if (open) {
      const id = requestAnimationFrame(() => meta.inputRef.current?.focus());
      return () => cancelAnimationFrame(id);
    }
  }, [open, meta.inputRef]);
  return null;
}

export interface FloatingPromptProps {
  onOpenHistory?: () => void;
}

export function FloatingPrompt({ onOpenHistory }: FloatingPromptProps) {
  const open = useFloatingPromptOpen();
  const { close } = useFloatingPromptActions();
  const [mounted, setMounted] = useState(false);

  // Drive enter/exit animation
  useEffect(() => {
    if (open) {
      const id = requestAnimationFrame(() => setMounted(true));
      return () => cancelAnimationFrame(id);
    }
    setMounted(false);
  }, [open]);

  useHotkey("Escape", close, { enabled: open, stopPropagation: true, conflictBehavior: "allow" });

  if (!open) return null;

  return (
    // biome-ignore lint/a11y/useKeyWithClickEvents: Escape handled via hotkey
    // biome-ignore lint/a11y/noStaticElementInteractions: backdrop overlay
    <div className="fixed inset-0 z-50" onClick={close}>
      {/* biome-ignore lint/a11y/useKeyWithClickEvents: stopPropagation only */}
      {/* biome-ignore lint/a11y/noStaticElementInteractions: container wrapper */}
      <div
        className={cn(
          "absolute bottom-4 left-1/2 -translate-x-1/2 w-full max-w-2xl px-4 transition-all duration-100",
          mounted ? "opacity-100 scale-100 translate-y-0" : "opacity-0 scale-95 translate-y-2",
        )}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="rounded-xl bg-background shadow-lg overflow-hidden border border-border">
          <AgentChat.Root>
            <OverlayAutoFocus open={open} />
            <AgentChat.Header>
              <AgentChat.NewChatButton />
              <AgentChat.HistoryButton onClick={onOpenHistory} />
              <AgentChat.DeleteButton />
            </AgentChat.Header>
            <FloatingMessages />
            <div className="p-3">
              <AgentChatSubjectBanner
                footer={
                  <InputGroupText className="text-xs">
                    <Kbd>ESC</Kbd> to close
                  </InputGroupText>
                }
              />
            </div>
          </AgentChat.Root>
        </div>
      </div>
    </div>
  );
}
